"use client";

import { Box, Button, HStack } from "@chakra-ui/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ReactNode } from "react";
import GenericIcon from "../actions/GenericIcon";
import { iconSizePresets } from "@/shared/config/iconSizePresets";

export type WithSwipeDragProps = {
  id: string;
  isMobile: boolean;
  children: ReactNode;
};

/**
 * 🧩 WithSwipeDrag
 * Simplified drag wrapper using dnd-kit's useSortable hook
 */
export default function WithSwipeDrag({
  id,
  isMobile,
  children,
}: WithSwipeDragProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : "auto",
    borderRadius: "6px",
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      width="full"
      bg={isDragging ? "moveon.inactive" : undefined}
    >
      {isMobile ? (
        // Mobile: Full area draggable
        <Box
          p="2"
          cursor="grab"
          _active={{
            cursor: "grabbing",
          }}
          {...attributes}
          {...listeners}
        >
          {children}
        </Box>
      ) : (
        // Desktop: Drag handle + content
        <HStack>
          <Box flex="1">{children}</Box>
          <Button
            type="button"
            size="sm"
            variant="outline"
            color="moveon.text.muted"
            borderColor="moveon.surface.interactiveBorder"
            cursor="grab"
            _active={{
              cursor: "grabbing",
            }}
            _hover={{
              bg: "moveon.surface.elevatedBg",
              borderColor: "moveon.selection.hoverBorder",
            }}
            px="1"
            aria-label="Drag to reorder"
            title="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GenericIcon
              icon="drag_and_drop"
              size={iconSizePresets.inline}
              humanReadable="Drag to reorder"
            />
          </Button>
        </HStack>
      )}
    </Box>
  );
}
